import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import config from '../config';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonText,
  IonSpinner,
  IonAlert,
  IonButton,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonChip,
  IonBadge,
  IonAccordion,
  IonAccordionGroup
} from '@ionic/react';
import { 
  medicalOutline, 
  documentTextOutline, 
  calendarOutline, 
  personOutline,
  checkmarkCircleOutline,
  warningOutline,
  informationCircleOutline,
  arrowBackOutline
} from 'ionicons/icons';
import './MedicalDataViewer.css';

const MedicalDataViewer = ({ showTestIdentifiers = false }) => {
  const history = useHistory();
  const apiUrl = config.apiUrl;

  // State management
  const [member_id, setMemberId] = useState(() => localStorage.getItem('member_id'));
  const [user_id, setUserId] = useState(() => localStorage.getItem('user_id'));
  const [auth_token, setAuthToken] = useState(() => localStorage.getItem('auth_token'));
  const [userFullName, setUserFullName] = useState('');
  const [activeTab, setActiveTab] = useState('medical-notes');
  const [medicalNotes, setMedicalNotes] = useState([]);
  const [labResults, setLabResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authError, setAuthError] = useState(null);

     // Add message event listener to receive auth data
     useEffect(() => {
       // Define allowed origins for postMessage
       const allowedOrigins = [
         'https://www.infinity-mc-hms.com',
         'https://infinity-mc-healthplan-edit-dot-infinity-medical-concier-724b0.lm.r.appspot.com',
         'http://localhost:3000',
         'http://localhost:5000',
         'https://localhost:3000',
         'https://localhost:5000'
       ];
        console.log('Setting up message listener...');
        console.log('Parent window exists:', !!window.parent);
        console.log('Is same origin:', window.parent === window);
        
        const handleMessage = (event) => {
          console.log('Received message:', {
            ...event,
            data: event.data?.target ? { target: event.data.target } : event.data
          });
          
          // Ignore MetaMask and webpack messages
          if (event.data?.target === 'metamask-contentscript' || 
              event.data?.target === 'metamask-inpage' ||
              event.data?.type === 'webpackWarnings') {
            return;
          }
    
          console.log('Processing message from origin:', event.origin);
          
          // Verify the origin of the message
          if (!allowedOrigins.includes(event.origin)) {
            console.log('Message origin not in allowed list:', event.origin);
            console.log('Allowed origins:', allowedOrigins);
            return;
          }
    
          // Verify the message structure
          if (event.data && event.data.type === 'AUTH_DATA') {
            const { member_id, user_id, auth_token, assessment_date: incomingAssessmentDate } = event.data;
            console.log('Auth data received:', { 
              member_id, 
              user_id, 
              auth_token: auth_token ? '***' : undefined,
              assessment_date: incomingAssessmentDate
            });
            
             if (member_id && user_id && auth_token) {
               console.log('Setting auth data...');
               localStorage.setItem('member_id', member_id);
               localStorage.setItem('user_id', user_id);
               localStorage.setItem('auth_token', auth_token);
               localStorage.setItem('assessment_date', incomingAssessmentDate);
               setMemberId(member_id);
               setUserId(user_id);
               setAuthToken(auth_token);
               setAuthError(null);
               console.log('Auth data successfully set');
             } else {
              console.error('Missing required auth data:', { 
                hasMemberId: !!member_id, 
                hasUserId: !!user_id, 
                hasAuthToken: !!auth_token 
              });
              setAuthError('Missing required authentication data. Please ensure you are properly logged in.');
            }
          }
        };
    
        window.addEventListener('message', handleMessage);
        
        // Request auth data from parent only if we don't have it
        if (!member_id || !user_id || !auth_token) {
          console.log('Requesting auth data from parent...');
          allowedOrigins.forEach(origin => {
            try {
              window.parent.postMessage({ type: 'REQUEST_AUTH' }, origin);
              console.log('Auth request sent to origin:', origin);
            } catch (error) {
              console.error('Error sending auth request to origin:', origin, error);
            }
          });
        } else {
          console.log('Already have auth data, skipping request'); 
        }
    
         return () => {
           console.log('Cleaning up message listener...');
           window.removeEventListener('message', handleMessage);
         };
       }, [member_id, user_id, auth_token]);

  // Load medical notes
  const loadMedicalNotes = useCallback(async () => {
    if (!member_id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(
        `${apiUrl}/medical_note_analysis/get/${member_id}`,
        {
          headers: {
            'Authorization': `Bearer ${auth_token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      setMedicalNotes(response.data || []);
    } catch (err) {
      console.error('Error loading medical notes:', err);
      setError('Failed to load medical notes. Please try again.');
      toast.error('Failed to load medical notes');
    } finally {
      setLoading(false);
    }
  }, [member_id, auth_token, apiUrl]);

  // Load lab results
  const loadLabResults = useCallback(async () => {
    if (!member_id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(
        `${apiUrl}/lab_results_analysis/get/${member_id}`,
        {
          headers: {
            'Authorization': `Bearer ${auth_token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      setLabResults(response.data || []);
    } catch (err) {
      console.error('Error loading lab results:', err);
      setError('Failed to load lab results. Please try again.');
      toast.error('Failed to load lab results');
    } finally {
      setLoading(false);
    }
  }, [member_id, auth_token, apiUrl]);
    
       // Add effect to fetch data when we have auth credentials
       useEffect(() => {
         if (!apiUrl) {
           console.error('API URL is not configured');
           setError('API URL is not configured');
           setLoading(false);
           return;
         }
      
         if (member_id && user_id && auth_token) {
           console.log('Starting data fetch with:', {
             hasMemberId: !!member_id,
             hasUserId: !!user_id,
             hasAuthToken: !!auth_token,     
             apiUrl
           });
           
           // Load data based on active tab
           if (activeTab === 'medical-notes') {
             loadMedicalNotes();
           } else if (activeTab === 'lab-results') {
             loadLabResults();
           }
         }
       }, [member_id, user_id, auth_token, apiUrl, activeTab, loadMedicalNotes, loadLabResults]);
    
      // Add effect to log state changes
      useEffect(() => {
        console.log('Auth state changed:', {
          hasMemberId: !!member_id,
          hasUserId: !!user_id,
          hasAuthToken: !!auth_token,
          isLoading: loading,
          hasError: !!error,
          hasAuthError: !!authError
        });
      }, [member_id, user_id, auth_token, loading, error, authError]);
    

  // Format date helper
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check authentication
  useEffect(() => {
    if (!member_id || !user_id || !auth_token) {
      setAuthError('Authentication required. Please log in again.');
      return;
    }
    setUserFullName(localStorage.getItem('userFullName') || '');
  }, [member_id, user_id, auth_token]);

  // Load data when tab changes (only if we have auth data)
  useEffect(() => {
    if (member_id && user_id && auth_token) {
      if (activeTab === 'medical-notes') {
        loadMedicalNotes();
      } else if (activeTab === 'lab-results') {
        loadLabResults();
      }
    }
  }, [activeTab, member_id, user_id, auth_token, loadMedicalNotes, loadLabResults]);

  // Handle tab change
  const handleTabChange = (e) => {
    setActiveTab(e.detail.value);
  };

  // Render medical notes
  const renderMedicalNotes = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <IonSpinner name="crescent" />
          <IonText>Loading medical notes...</IonText>
        </div>
      );
    }

    if (medicalNotes.length === 0) {
      return (
        <IonCard>
          <IonCardContent>
            <IonText color="medium">
              <IonIcon icon={informationCircleOutline} />
              No medical notes found for this member.
            </IonText>
          </IonCardContent>
        </IonCard>
      );
    }

    return medicalNotes.map((note, index) => (
      <IonCard key={note.id || index} className="medical-note-card">
        <IonCardHeader>
          <IonCardTitle>
            <IonIcon icon={documentTextOutline} />
            {showTestIdentifiers ? `Medical Note #${note.id || index + 1}` : 'Medical Note'}
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          <IonGrid>
            <IonRow>
              <IonCol size="12" size-md="6">
                <IonItem>
                  <IonIcon icon={calendarOutline} slot="start" />
                  <IonLabel>
                    <h3>Date Created</h3>
                    <p>{formatDate(note.created_at)}</p>
                  </IonLabel>
                </IonItem>
              </IonCol>
              <IonCol size="12" size-md="6">
                <IonItem>
                  <IonIcon icon={personOutline} slot="start" />
                  <IonLabel>
                    <h3>Assessment Date</h3>
                    <p>{note.analysis?.date_of_assessment || 'N/A'}</p>
                  </IonLabel>
                </IonItem>
              </IonCol>
            </IonRow>
          </IonGrid>

          {note.analysis?.diagnosis && (
            <IonItem>
              <IonIcon icon={warningOutline} slot="start" color="warning" />
              <IonLabel>
                <h3>Diagnosis</h3>
                <p>{note.analysis.diagnosis}</p>
              </IonLabel>
            </IonItem>
          )}

          {note.analysis?.medical_notes && (
            <IonItem>
              <IonIcon icon={documentTextOutline} slot="start" />
              <IonLabel>
                <h3>Medical Notes</h3>
                <p>{note.analysis.medical_notes}</p>
              </IonLabel>
            </IonItem>
          )}

          {note.analysis?.medications && note.analysis.medications.length > 0 && (
            <IonAccordionGroup>
              <IonAccordion>
                <IonItem slot="header">
                  <IonIcon icon={medicalOutline} slot="start" />
                  <IonLabel>Medications ({note.analysis.medications.length})</IonLabel>
                </IonItem>
                <div slot="content">
                  {note.analysis.medications.map((med, medIndex) => (
                    <IonItem key={medIndex}>
                      <IonLabel>
                        <h3>{med.name}</h3>
                        {med.dosage && <p>Dosage: {med.dosage}</p>}
                        {med.frequency && <p>Frequency: {med.frequency}</p>}
                      </IonLabel>
                    </IonItem>
                  ))}
                </div>
              </IonAccordion>
            </IonAccordionGroup>
          )}

          {note.analysis?.procedures && note.analysis.procedures.length > 0 && (
            <IonAccordionGroup>
              <IonAccordion>
                <IonItem slot="header">
                  <IonIcon icon={checkmarkCircleOutline} slot="start" />
                  <IonLabel>Procedures ({note.analysis.procedures.length})</IonLabel>
                </IonItem>
                <div slot="content">
                  {note.analysis.procedures.map((proc, procIndex) => (
                    <IonItem key={procIndex}>
                      <IonLabel>
                        <h3>{proc.type}</h3>
                        {proc.site && <p>Site: {proc.site}</p>}
                        {proc.date && <p>Date: {proc.date}</p>}
                        {proc.outcome && <p>Outcome: {proc.outcome}</p>}
                      </IonLabel>
                    </IonItem>
                  ))}
                </div>
              </IonAccordion>
            </IonAccordionGroup>
          )}

          {note.analysis?.risk_factors && note.analysis.risk_factors.length > 0 && (
            <IonAccordionGroup>
              <IonAccordion>
                <IonItem slot="header">
                  <IonIcon icon={warningOutline} slot="start" color="danger" />
                  <IonLabel>Risk Factors ({note.analysis.risk_factors.length})</IonLabel>
                </IonItem>
                <div slot="content">
                  {note.analysis.risk_factors.map((risk, riskIndex) => (
                    <IonChip key={riskIndex} color="warning">
                      <IonLabel>{risk}</IonLabel>
                    </IonChip>
                  ))}
                </div>
              </IonAccordion>
            </IonAccordionGroup>
          )}

          {note.analysis?.investigations && note.analysis.investigations.length > 0 && (
            <IonAccordionGroup>
              <IonAccordion>
                <IonItem slot="header">
                  <IonIcon icon={informationCircleOutline} slot="start" />
                  <IonLabel>Investigations ({note.analysis.investigations.length})</IonLabel>
                </IonItem>
                <div slot="content">
                  {note.analysis.investigations.map((investigation, invIndex) => (
                    <IonItem key={invIndex}>
                      <IonLabel>
                        <h3>{investigation.type}</h3>
                        {investigation.date && <p>Date: {investigation.date}</p>}
                        {investigation.findings && <p>Findings: {investigation.findings}</p>}
                      </IonLabel>
                    </IonItem>
                  ))}
                </div>
              </IonAccordion>
            </IonAccordionGroup>
          )}

          {note.analysis?.impression_or_comment && (
            <IonItem>
              <IonIcon icon={informationCircleOutline} slot="start" />
              <IonLabel>
                <h3>Impression/Comment</h3>
                <p>{note.analysis.impression_or_comment}</p>
              </IonLabel>
            </IonItem>
          )}
        </IonCardContent>
      </IonCard>
    ));
  };

  // Render lab results
  const renderLabResults = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <IonSpinner name="crescent" />
          <IonText>Loading lab results...</IonText>
        </div>
      );
    }

    if (labResults.length === 0) {
      return (
        <IonCard>
          <IonCardContent>
            <IonText color="medium">
              <IonIcon icon={informationCircleOutline} />
              No lab results found for this member.
            </IonText>
          </IonCardContent>
        </IonCard>
      );
    }

    return labResults.map((result, index) => (
      <IonCard key={index} className="lab-result-card">
        <IonCardHeader>
          <IonCardTitle>
            <IonIcon icon={medicalOutline} />
            Lab Analysis #{index + 1}
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          <IonItem>
            <IonIcon icon={calendarOutline} slot="start" />
            <IonLabel>
              <h3>Analysis Date</h3>
              <p>{formatDate(result.created_at)}</p>
            </IonLabel>
          </IonItem>

          {result.analysis?.meta && (
            <IonItem>
              <IonIcon icon={informationCircleOutline} slot="start" />
              <IonLabel>
                <h3>Source</h3>
                <p>Parser: {result.analysis.meta.parser}</p>
                <p>Source: {result.analysis.meta.source_key}</p>
                {result.analysis.meta.parsed_date && (
                  <p>Parsed: {result.analysis.meta.parsed_date}</p>
                )}
              </IonLabel>
            </IonItem>
          )}

          {result.analysis?.pathology_results && result.analysis.pathology_results.length > 0 && (
            <IonAccordionGroup>
              <IonAccordion>
                <IonItem slot="header">
                  <IonIcon icon={documentTextOutline} slot="start" />
                  <IonLabel>Pathology Results ({result.analysis.pathology_results.length} tests)</IonLabel>
                </IonItem>
                <div slot="content">
                  {(() => {
                    // Group results by category
                    const groupedResults = result.analysis.pathology_results.reduce((acc, test) => {
                      const category = test.Category || 'Other';
                      if (!acc[category]) {
                        acc[category] = [];
                      }
                      acc[category].push(test);
                      return acc;
                    }, {});

                    return Object.entries(groupedResults).map(([category, tests]) => {
                      // Get dates that have data for this specific category
                      const categoryDates = new Set();
                      tests.forEach(test => {
                        Object.keys(test).forEach(key => {
                          if (key !== 'Section' && key !== 'Category' && key !== 'Test Name' && key !== 'Reference Range' && test[key] && test[key].trim() !== '') {
                            categoryDates.add(key);
                          }
                        });
                      });
                      
                      // Sort dates chronologically
                      const sortedDates = Array.from(categoryDates).sort((a, b) => new Date(a) - new Date(b));

                      return (
                        <div key={category} className="category-table-container">
                          <h4 className="category-title">{category}</h4>
                          <div className="lab-results-table">
                            <table>
                              <thead>
                                <tr>
                                  <th>Test Name</th>
                                  {sortedDates.map(date => (
                                    <th key={date}>{date}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {tests.map((test, testIndex) => (
                                  <tr key={testIndex}>
                                    <td className="test-name">{test['Test Name']}</td>
                                    {sortedDates.map(date => (
                                      <td key={date} className="test-value">
                                        {test[date] || ''}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </IonAccordion>
            </IonAccordionGroup>
          )}

          {result.analysis?.excluded_data && result.analysis.excluded_data.total_excluded > 0 && (
            <IonItem>
              <IonIcon icon={warningOutline} slot="start" color="warning" />
              <IonLabel>
                <h3>Excluded Data</h3>
                <p>Total excluded: {result.analysis.excluded_data.total_excluded}</p>
              </IonLabel>
            </IonItem>
          )}
        </IonCardContent>
      </IonCard>
    ));
  };

  if (authError) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Medical Data Viewer</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <IonAlert
            isOpen={true}
            header="Authentication Error"
            message={authError}
            buttons={[
              {
                text: 'Go Back',
                handler: () => history.goBack()
              }
            ]}
          />
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButton fill="clear" slot="start" onClick={() => history.goBack()}>
            <IonIcon icon={arrowBackOutline} />
          </IonButton>
          <IonTitle>Medical Data Viewer</IonTitle>
          {userFullName && (
            <IonText slot="end" className="user-name">
              {userFullName}
            </IonText>
          )}
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div className="medical-data-container">
          <IonSegment value={activeTab} onIonChange={handleTabChange}>
            <IonSegmentButton value="medical-notes">
              <IonIcon icon={documentTextOutline} />
              <IonLabel>Medical Notes</IonLabel>
              {medicalNotes.length > 0 && (
                <IonBadge color="primary">{medicalNotes.length}</IonBadge>
              )}
            </IonSegmentButton>
            <IonSegmentButton value="lab-results">
              <IonIcon icon={medicalOutline} />
              <IonLabel>Lab Results</IonLabel>
              {labResults.length > 0 && (
                <IonBadge color="secondary">{labResults.length}</IonBadge>
              )}
            </IonSegmentButton>
          </IonSegment>

          {error && (
            <IonCard color="danger">
              <IonCardContent>
                <IonText color="light">
                  <IonIcon icon={warningOutline} />
                  {error}
                </IonText>
              </IonCardContent>
            </IonCard>
          )}

          <div className="tab-content">
            {activeTab === 'medical-notes' && renderMedicalNotes()}
            {activeTab === 'lab-results' && renderLabResults()}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default MedicalDataViewer;
